import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, MenuItem, Alert, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import { Save, ArrowBack, Add, Delete } from '@mui/icons-material';
import procurementService from '../../services/procurement.service';
import settingsService from '../../services/settings.service';
import { useLanguage } from '../../i18n/LanguageContext';
import api from '../../services/api';

interface POLine {
  productId: number | '';
  productName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

const emptyLine: POLine = { productId: '', productName: '', quantity: 1, unitPrice: 0, taxRate: 0 };

const PurchaseOrderForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isView = Boolean(id);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [viewData, setViewData] = useState<any>(null);
  const [form, setForm] = useState({
    orderDate: new Date().toISOString().split('T')[0],
    supplierId: '' as any, expectedDeliveryDate: '', notes: '',
    currencyCode: 'USD', exchangeRate: 1
  });
  const [lines, setLines] = useState<POLine[]>([{ ...emptyLine }]);

  useEffect(() => { loadData(); if (isView) loadOrder(); }, []);

  const loadData = async () => {
    try {
      const [supRes, prodRes] = await Promise.all([
        procurementService.getSuppliers(), api.get('/products')
      ]);
      setSuppliers(supRes.data); setProducts(prodRes.data);
    } catch (err) { console.error('Error loading base data:', err); }

    try {
      const currRes = await settingsService.getCurrencies();
      setCurrencies(currRes.data);
      const def = currRes.data.find((c: any) => c.isDefault);
      if (def && !isView) setForm(f => ({ ...f, currencyCode: def.code }));
    } catch (err) { console.error('Currencies not available:', err); }
  };

  const loadOrder = async () => {
    try {
      const res = await procurementService.getPurchaseOrder(Number(id));
      setViewData(res.data);
      setForm({
        orderDate: res.data.orderDate.split('T')[0], supplierId: res.data.supplierId,
        expectedDeliveryDate: res.data.expectedDeliveryDate?.split('T')[0] || '',
        notes: res.data.notes || '', currencyCode: res.data.currencyCode || 'USD',
        exchangeRate: res.data.exchangeRate || 1
      });
      if (res.data.lines) setLines(res.data.lines.map((l: any) => ({
        productId: l.productId, productName: l.productName,
        quantity: l.quantity, unitPrice: l.unitPrice, taxRate: l.taxRate
      })));
    } catch (err) { console.error(err); }
  };

  const updateLine = (index: number, field: keyof POLine, value: any) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'productId') {
      const product = products.find((p: any) => p.id === value);
      if (product) { updated[index].productName = product.name; updated[index].unitPrice = product.unitPrice || 0; }
    }
    setLines(updated);
  };

  const addLine = () => setLines([...lines, { ...emptyLine }]);
  const removeLine = (i: number) => { if (lines.length > 1) setLines(lines.filter((_, idx) => idx !== i)); };

  const subTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const taxTotal = lines.reduce((s, l) => s + l.quantity * l.unitPrice * l.taxRate / 100, 0);
  const grandTotal = subTotal + taxTotal;
  const sym = currencies.find(c => c.code === form.currencyCode)?.symbol || '$';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    const validLines = lines.filter(l => l.productId && l.quantity > 0);
    if (!validLines.length) { setError('At least one line item is required'); return; }
    try {
      await procurementService.createPurchaseOrder({
        ...form, expectedDeliveryDate: form.expectedDeliveryDate || null, lines: validLines
      });
      navigate('/purchase-orders');
    } catch (err: any) { setError(err.response?.data?.message || 'Error creating purchase order'); }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{isView ? `Purchase Order: ${viewData?.orderNumber || ''}` : 'New Purchase Order'}</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/purchase-orders')}>Back</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {viewData && <Alert severity={viewData.status === 'RECEIVED' ? 'success' : viewData.status === 'CANCELLED' ? 'error' : 'info'} sx={{ mb: 2 }}>Status: {viewData.status}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required type="date" label={t('purchase_orders.order_date')} value={form.orderDate}
          onChange={e => setForm({ ...form, orderDate: e.target.value })} InputLabelProps={{ shrink: true }} disabled={isView} />
        <TextField fullWidth required select label={t('purchase_orders.supplier')} value={form.supplierId}
          onChange={e => setForm({ ...form, supplierId: e.target.value })} disabled={isView}>
          <MenuItem value="">Select Supplier</MenuItem>
          {suppliers.map((s: any) => <MenuItem key={s.id} value={s.id}>{s.code} - {s.name}</MenuItem>)}
        </TextField>
        <TextField select label={t('common.currency')} value={form.currencyCode} sx={{ minWidth: 150 }}
          onChange={e => setForm({ ...form, currencyCode: e.target.value })} disabled={isView}>
          {currencies.map((c: any) => <MenuItem key={c.id} value={c.code}>{c.code} ({c.symbol})</MenuItem>)}
          {currencies.length === 0 && <MenuItem value="USD">USD ($)</MenuItem>}
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth type="date" label={t('purchase_orders.expected_delivery')} value={form.expectedDeliveryDate}
          onChange={e => setForm({ ...form, expectedDeliveryDate: e.target.value })} InputLabelProps={{ shrink: true }} disabled={isView} />
        <TextField fullWidth label={t('common.notes')} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} disabled={isView} />
      </Box>

      <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>Order Lines</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>{t('sales_orders.product')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.quantity')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.unit_price')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('sales_orders.tax_pct')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('common.line_total')}</TableCell>
            {isView && <TableCell sx={{ fontWeight: 'bold' }} align="right">Received</TableCell>}
            {!isView && <TableCell sx={{ width: 50 }} />}
          </TableRow></TableHead>
          <TableBody>
            {lines.map((line, idx) => {
              const lt = line.quantity * line.unitPrice; const tax = lt * line.taxRate / 100;
              return (<TableRow key={idx}>
                <TableCell>{isView ? line.productName : (
                  <TextField select fullWidth size="small" value={line.productId} onChange={e => updateLine(idx, 'productId', e.target.value)}>
                    <MenuItem value="">Select Product</MenuItem>
                    {products.map((p: any) => <MenuItem key={p.id} value={p.id}>{p.sku || p.name} - {p.name}</MenuItem>)}
                  </TextField>)}</TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.quantity}
                  onChange={e => updateLine(idx, 'quantity', Number(e.target.value))} inputProps={{ min: 1 }} disabled={isView} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.unitPrice}
                  onChange={e => updateLine(idx, 'unitPrice', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} disabled={isView} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.taxRate}
                  onChange={e => updateLine(idx, 'taxRate', Number(e.target.value))} inputProps={{ step: '0.01', min: 0 }} disabled={isView} /></TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{(lt + tax).toFixed(2)}</TableCell>
                {isView && viewData?.lines && <TableCell align="right">
                  <Chip label={`${viewData.lines[idx]?.receivedQuantity || 0} / ${line.quantity}`}
                    color={viewData.lines[idx]?.receivedQuantity >= line.quantity ? 'success' : 'warning'} size="small" />
                </TableCell>}
                {!isView && <TableCell><IconButton size="small" color="error" onClick={() => removeLine(idx)} disabled={lines.length <= 1}><Delete fontSize="small" /></IconButton></TableCell>}
              </TableRow>);
            })}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}><TableCell colSpan={3} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{subTotal.toFixed(2)}</TableCell>{(isView || true) && <TableCell />}{!isView && <TableCell />}</TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}><TableCell colSpan={3} /><TableCell align="right" sx={{ fontWeight: 'bold' }}>Tax:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{taxTotal.toFixed(2)}</TableCell>{(isView || true) && <TableCell />}{!isView && <TableCell />}</TableRow>
            <TableRow sx={{ bgcolor: '#e3f2fd' }}><TableCell colSpan={3} /><TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>Total:</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{sym}{grandTotal.toFixed(2)}</TableCell>{(isView || true) && <TableCell />}{!isView && <TableCell />}</TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {!isView && (<>
        <Button startIcon={<Add />} onClick={addLine} sx={{ mt: 1 }}>{t('common.add_line')}</Button>
        <Box sx={{ mt: 3 }}><Button type="submit" variant="contained" startIcon={<Save />} size="large">{t('purchase_orders.create')}</Button></Box>
      </>)}
    </Box>
  );
};

export default PurchaseOrderForm;
